import { ROUTES } from '@/constants/constants';
import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { fireSubscribedConversion, logEvent } from '@/helpers/eventLogger';
import { useNotifications } from '@/hooks/useNotifications';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Box, Button, CircularProgress } from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { confirmPaymentIntent, createSetupIntent, getStripePublicKey, processPayment } from '@tailored-pdf/api';
import { FormEvent, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FormSkeleton } from './FormSkeleton';

type Props = {
  documentId?: string;
  subscriptionType: string;
  onSubscriptionSuccess?: () => void;
  onCancel?: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
  billingInfo?: React.ReactNode;
};

type FormProps = {
  documentId?: string;
  subscriptionType: string;
  onPaymentError?: (message: string) => void;
  onPaymentSuccess?: () => void;
  onCancel?: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
  billingInfo?: React.ReactNode;
};

function LinkWrapper({ onClick, to, children }: PropsWithChildren<{ to: string; onClick?: () => void }>) {
  return (
    <Link onClick={onClick} to={to}>
      <Box sx={{ color: 'primary.main' }} display={'inline'}>
        {children}
      </Box>
    </Link>
  );
}

function CheckoutForm({
  documentId,
  subscriptionType,
  onPaymentError,
  onPaymentSuccess,
  onCancel,
  onSubmittingChange,
  billingInfo,
}: FormProps) {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const { t: tt } = useTranslation('web');
  const stripe = useStripe();
  const elements = useElements();
  const isMobile = useIsMobile();

  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch the client IP once on mount and keep the promise, so it is ready by
  // submit time without adding a third-party round-trip to the payment-critical
  // path. The IP is required for `mandate_data.customer_acceptance.online`.
  const ipPromiseRef = useRef<Promise<string>>();
  const ensureIp = useCallback((): Promise<string> => {
    if (!ipPromiseRef.current) {
      const fetchIp = (): Promise<string> =>
        fetch('https://api.ipify.org?format=json')
          .then((response) => response.json())
          .then(({ ip }) => ip as string);

      // Retry once on failure; clear the cache on a final failure so a later
      // submit re-attempts instead of being stuck on a rejected promise.
      ipPromiseRef.current = fetchIp()
        .catch(() => fetchIp())
        .catch((e) => {
          ipPromiseRef.current = undefined;
          throw e;
        });
    }
    return ipPromiseRef.current;
  }, []);

  useEffect(() => {
    void ensureIp().catch(() => undefined);
  }, [ensureIp]);

  // Mirror the in-flight state up so the dialog can refuse to close mid-submit
  // (closing would unmount <Elements> and destroy the PaymentElement, which
  // makes the in-flight Stripe calls throw "This Element has already been destroyed").
  useEffect(() => {
    onSubmittingChange?.(loading);
  }, [loading, onSubmittingChange]);

  useEffect(() => () => onSubmittingChange?.(false), [onSubmittingChange]);

  // If the form unmounts mid-submit (e.g. navigation, or `needPayment` toggled
  // elsewhere) the PaymentElement is destroyed; don't start further Stripe calls
  // against it. Step 1 already blocks the common case (dialog dismiss).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements || !loaded || !ready) {
      return;
    }

    logEvent({ logType: 'EVENT', eventType: 'CHECKOUT_FORM_SUBMITTED' });
    setLoading(true);

    try {
      const ip = await ensureIp();
      if (!mountedRef.current) return;

      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${location.origin}${ROUTES.subscribed(i18n.language)}?tariff=${subscriptionType}&documentId=${documentId ?? ''
            }`,
          mandate_data: {
            customer_acceptance: {
              type: 'online',
              online: {
                ip_address: ip,
                user_agent: window.navigator.userAgent,
              },
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        //by code
        // if (result.error.code === 'setup_intent_unexpected_state') {
        //   //You cannot confirm this SetupIntent because it has already succeeded.
        //   onPaymentError?.(result.error.message ?? t('error'));
        //   return;
        // } else if (result.error.code === 'setup_intent_authentication_failure') {
        //   //We are unable to authenticate your payment method. Please choose a different payment method and try again.
        //   onPaymentError?.(result.error.message ?? t('error'));
        //   return;
        // }

        // if (result.error.type === 'invalid_request_error') {
        //   onPaymentError?.(tt('common.error'));
        // }
        if (result.error.type === 'api_connection_error') {
          onPaymentError?.(tt('common.networkError'));
        } else {
          onPaymentError?.(result.error.message ?? t('error'));
        }
      } else {
        const { payment_method } = result.setupIntent;

        if (payment_method) {
          const paymentResponse = await processPayment({
            paymentMethodCode: typeof payment_method === 'string' ? payment_method : '', //payment_method.id,
            subscriptionType,
          });

          if (paymentResponse.data.stripeId) {
            if (!mountedRef.current) return;
            const paymentResult = await stripe.confirmCardPayment(paymentResponse.data.stripeId);

            if (paymentResult.paymentIntent) {
              const paymentIntentId = paymentResult.paymentIntent.id;

              const confirmPaymentResponse = await confirmPaymentIntent({
                paymentIntentId: paymentIntentId,
                subscriptionType: subscriptionType,
              });

              if (confirmPaymentResponse.status !== 200) {
                console.error('Payment confirmation error:', confirmPaymentResponse);
                onPaymentError?.('Error confirming payment, please try again.');
              } else {
                onPaymentSuccess?.();
              }
            }

            if (paymentResult.error) {
              console.error('Payment confirmation error:', paymentResult.error);
              onPaymentError?.(paymentResult.error.message || tt('common.error'));
            } else {
              onPaymentSuccess?.();
            }
          } else {
            onPaymentError?.(tt('common.error'));
          }
        } else {
          onPaymentError?.(tt('common.error'));
        }
      }
    } catch (e) {
      onPaymentError?.(getResponseError(e));
    } finally {
      setLoading(false);
    }
  };

  const buttonsBar = (
    <Box
      paddingTop={6}
      display="flex"
      justifyContent="start"
      gap={3}
      sx={
        isMobile
          ? {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
            backgroundColor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
          }
          : {}
      }
    >
      <Button
        variant="contained"
        type="submit"
        disableElevation
        disabled={!stripe || !elements || !loaded || !ready || loading}
        startIcon={loading ? <CircularProgress size={20} /> : undefined}
        fullWidth={isMobile}
        sx={{ position: isMobile ? 'static' : 'relative' }}
      >
        <span>{t('subscribe')}</span>
      </Button>
      {!isMobile && (
        <Button variant="outlined" onClick={onCancel} disabled={!stripe || !elements || !loaded || loading}>
          <span>{t('cancel')}</span>
        </Button>
      )}
    </Box>
  );

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={isMobile ? { paddingBottom: 24 } : {}}>
        {stripe && elements ? (
          <PaymentElement
            onLoaderStart={() => setReady(true)}
            onReady={() => setLoaded(true)}
            options={{
              readOnly: loading,
              fields: {
                billingDetails: {
                  address: {},
                },
              },
            }}
          />
        ) : (
          <FormSkeleton />
        )}

        {isMobile && billingInfo && <Box marginTop={2}>{billingInfo}</Box>}

        {!ready && <FormSkeleton />}
        {loaded && (
          <Box sx={{ color: 'text.disabled' }} fontSize={'12px'}>
            <Trans
              t={t}
              i18nKey="policy"
              components={{
                terms: <LinkWrapper to={ROUTES.terms(i18n.language)} onClick={onCancel} />,
                policy: <LinkWrapper to={ROUTES.policy(i18n.language)} onClick={onCancel} />,
              }}
            />
          </Box>
        )}
      </Box>
      {buttonsBar}
    </form>
  );
}

function PaymentForm({ documentId, subscriptionType, onSubscriptionSuccess, onCancel, onSubmittingChange, billingInfo }: Props) {
  const { t } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const { notifySuccess, notifyError } = useNotifications();
  const {
    state: { userInfo },
  } = useContext(Context);

  const [options, setOptions] = useState<StripeElementsOptions>();
  const [stripe, setStripe] = useState<Stripe | null>(null);

  const handlePaymentSuccess = useCallback(() => {
    fireSubscribedConversion(userInfo?.email);
    logEvent({ logType: 'EVENT', eventType: 'CHECKOUT_SUCCEEDED' });
    notifySuccess(t('success'));
    onSubscriptionSuccess?.();
  }, [notifySuccess, onSubscriptionSuccess, t, userInfo?.email]);

  const loadIntent = useCallback(() => {
    createSetupIntent()
      .then((setupResp) =>
        setOptions({
          clientSecret: setupResp.data,
          appearance: {
            disableAnimations: true,
            variables: {
              borderRadius: '8px',
              fontSize3Xs: '12px',
              fontFamily: 'Inter,Roboto,Helvetica,Arial,sans-serif',
              colorTextSecondary: '#667085',
              colorPrimary: '#4B68FF',
            },
            rules: {
              '.Label': {
                fontWeight: '600',
              },
            },
          },
        }),
      )
      .catch((e) => notifyError(getResponseError(e)));
  }, [notifyError]);

  const handleChangeIntent = useCallback(
    (message: string) => {
      logEvent({ logType: 'ERROR', eventType: 'CHECKOUT_ERROR', trace: message });
      notifyError(message);
      setOptions(undefined);
      loadIntent();
    },
    [loadIntent, notifyError],
  );

  useEffect(() => {
    getStripePublicKey()
      .then((keyResp) => loadStripe(keyResp.data))
      .then(setStripe)
      .catch((e) => notifyError(getResponseError(e)));

    loadIntent();
  }, [loadIntent, notifyError]);

  return options ? (
    <Elements stripe={stripe} options={options}>
      <CheckoutForm
        documentId={documentId}
        subscriptionType={subscriptionType}
        onPaymentError={handleChangeIntent}
        onPaymentSuccess={handlePaymentSuccess}
        onCancel={onCancel}
        onSubmittingChange={onSubmittingChange}
        billingInfo={billingInfo}
      />
    </Elements>
  ) : (
    <FormSkeleton withButton />
  );
}

export { PaymentForm };
